/**
 * `useReliability()` — the reliability engines' context, for React.
 *
 * Every reliability engine is a pure function of
 * `{ ...ReliabilityInputs, attribution }`. Building that is two steps: fetch
 * the five lazy domains ({@link loadReliabilityInputs}), then run
 * {@link attribute} over them — the one expensive step (≈ 2,200 shift
 * assignments, ≈ 3,500 events). Both happen **once per page load**, behind a
 * module-level promise, so every scene that mounts this hook shares the same
 * `ctx` object and a second mount paints without a loading frame.
 *
 * A failed fetch clears the cache, so the next mount retries rather than
 * replaying the rejection forever.
 *
 * Engine binding (layer 2 → 4 seam): no derivation here — scenes pass `ctx`
 * to the pure engine functions (`driverTree`, `priorities`, `rootCause`, …)
 * inside their own `useMemo`.
 *
 * @see docs/engines/reliability.md
 */

import { useEffect, useState } from 'react';
import { attribute, type Attribution } from './attribution.ts';
import { loadReliabilityInputs, type ReliabilityInputs } from './inputs.ts';

/** What every reliability engine reads: the inputs plus the attribution. */
export type ReliabilityCtx = ReliabilityInputs & { readonly attribution: Attribution };

let pending: Promise<ReliabilityCtx> | null = null;
let resolved: ReliabilityCtx | null = null;

/** Fetch (or join the fetch of) the inputs and attribute them, once. */
export function loadReliabilityCtx(): Promise<ReliabilityCtx> {
  if (resolved) return Promise.resolve(resolved);
  if (!pending) {
    pending = loadReliabilityInputs()
      .then((inputs) => {
        resolved = { ...inputs, attribution: attribute(inputs) };
        return resolved;
      })
      .catch((err: unknown) => {
        pending = null;
        throw err instanceof Error ? err : new Error(String(err));
      });
  }
  return pending;
}

/** The shared reliability context, with loading and error state. */
export function useReliability(): {
  ctx: ReliabilityCtx | null;
  loading: boolean;
  error: Error | null;
} {
  const [ctx, setCtx] = useState<ReliabilityCtx | null>(resolved);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (ctx) return;
    let live = true;
    loadReliabilityCtx().then(
      (value) => {
        if (live) setCtx(value);
      },
      (err: Error) => {
        if (live) setError(err);
      },
    );
    return () => {
      live = false;
    };
  }, [ctx]);

  return { ctx, loading: !ctx && !error, error };
}
